import type { ReactElement } from 'react';

const HERO_DOT_KEYS = ['hd-1', 'hd-2', 'hd-3', 'hd-4', 'hd-5'] as const;
const POSTER_ROW_KEYS = [
  'pr-1',
  'pr-2',
  'pr-3',
  'pr-4',
  'pr-5',
  'pr-6',
  'pr-7',
  'pr-8',
] as const;
const SECONDARY_POSTER_KEYS = [
  'sp-1',
  'sp-2',
  'sp-3',
  'sp-4',
  'sp-5',
  'sp-6',
] as const;
const TOP_LIST_KEYS = ['tl-1', 'tl-2', 'tl-3', 'tl-4', 'tl-5'] as const;
const PARTNER_KEYS = ['pa-1', 'pa-2', 'pa-3', 'pa-4'] as const;
const NEWS_KEYS = ['ne-1', 'ne-2', 'ne-3'] as const;

function HeroSkeleton(): ReactElement {
  return (
    <div className="skeleton-shimmer relative h-[290px] w-full overflow-hidden sm:h-[420px] lg:h-[520px]">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background/80" />
      <div className="absolute bottom-6 left-4 right-4 space-y-3 sm:bottom-10 sm:left-10 sm:max-w-xl">
        <div className="skeleton-shimmer h-4 w-24 rounded" />
        <div className="skeleton-shimmer h-8 w-3/4 rounded sm:h-10" />
        <div className="skeleton-shimmer h-3 w-full rounded" />
        <div className="skeleton-shimmer h-3 w-5/6 rounded" />
        <div className="flex gap-2 pt-2">
          <div className="skeleton-shimmer h-10 w-32 rounded-full" />
          <div className="skeleton-shimmer h-10 w-32 rounded-full" />
        </div>
      </div>
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 sm:bottom-5">
        {HERO_DOT_KEYS.map((key) => (
          <div key={key} className="skeleton-shimmer h-1.5 w-6 rounded-full" />
        ))}
      </div>
    </div>
  );
}

function PosterRow({ titleWidth }: { titleWidth: string }): ReactElement {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className={`skeleton-shimmer h-6 rounded ${titleWidth}`} />
        <div className="skeleton-shimmer h-4 w-16 rounded" />
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
        {POSTER_ROW_KEYS.map((key) => (
          <div key={key} className="space-y-2">
            <div className="skeleton-shimmer aspect-[2/3] w-full rounded-lg border border-border/40" />
            <div className="skeleton-shimmer h-3 w-5/6 rounded" />
            <div className="skeleton-shimmer h-3 w-3/5 rounded" />
          </div>
        ))}
      </div>
    </section>
  );
}

function MangaPosterRow({ titleWidth }: { titleWidth: string }): ReactElement {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className={`skeleton-shimmer h-6 rounded ${titleWidth}`} />
        <div className="skeleton-shimmer h-4 w-16 rounded" />
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {SECONDARY_POSTER_KEYS.map((key) => (
          <div key={key} className="space-y-2">
            <div className="skeleton-shimmer aspect-[2/3] w-full rounded-lg border border-border/40" />
            <div className="skeleton-shimmer h-3 w-5/6 rounded" />
          </div>
        ))}
      </div>
    </section>
  );
}

function GenresSkeleton(): ReactElement {
  const tiles = [
    'g1',
    'g2',
    'g3',
    'g4',
    'g5',
    'g6',
    'g7',
    'g8',
    'g9',
    'g10',
    'g11',
    'g12',
  ];
  return (
    <section className="space-y-3">
      <div className="skeleton-shimmer h-6 w-44 rounded" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {tiles.map((key) => (
          <div
            key={key}
            className="skeleton-shimmer h-16 rounded-lg border border-border/40"
          />
        ))}
      </div>
    </section>
  );
}

function SidebarCard({
  titleWidth,
  children,
}: {
  titleWidth: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <section className="rounded-xl border border-border/60 bg-card/60 p-4">
      <div className={`skeleton-shimmer mb-3 h-5 rounded ${titleWidth}`} />
      {children}
    </section>
  );
}

function SidebarTopList(): ReactElement {
  return (
    <SidebarCard titleWidth="w-28">
      <div className="space-y-3">
        {TOP_LIST_KEYS.map((key) => (
          <div key={key} className="flex items-center gap-3">
            <div className="skeleton-shimmer h-12 w-9 flex-shrink-0 rounded" />
            <div className="flex-1 space-y-1.5">
              <div className="skeleton-shimmer h-3 w-4/5 rounded" />
              <div className="skeleton-shimmer h-3 w-1/2 rounded" />
            </div>
          </div>
        ))}
      </div>
    </SidebarCard>
  );
}

function SidebarPartners(): ReactElement {
  return (
    <SidebarCard titleWidth="w-32">
      <div className="grid grid-cols-2 gap-2">
        {PARTNER_KEYS.map((key) => (
          <div
            key={key}
            className="skeleton-shimmer h-14 rounded-md border border-border/40"
          />
        ))}
      </div>
    </SidebarCard>
  );
}

function SidebarNews(): ReactElement {
  return (
    <SidebarCard titleWidth="w-20">
      <div className="space-y-3">
        <div className="skeleton-shimmer aspect-video w-full rounded-md" />
        {NEWS_KEYS.map((key) => (
          <div key={key} className="flex gap-2">
            <div className="skeleton-shimmer h-12 w-16 flex-shrink-0 rounded" />
            <div className="flex-1 space-y-1.5">
              <div className="skeleton-shimmer h-3 w-full rounded" />
              <div className="skeleton-shimmer h-3 w-2/3 rounded" />
            </div>
          </div>
        ))}
      </div>
    </SidebarCard>
  );
}

function SidebarCommunities(): ReactElement {
  return (
    <SidebarCard titleWidth="w-36">
      <div className="space-y-2">
        <div className="skeleton-shimmer h-9 w-full rounded-md" />
        <div className="skeleton-shimmer h-9 w-full rounded-md" />
      </div>
    </SidebarCard>
  );
}

export function DiscoverSkeleton(): ReactElement {
  // No `<DelayedSkeleton>` wrapper here on purpose: when this skeleton
  // is used as a Suspense fallback the wait is already applied once
  // at the RouteSkeleton level. When the lazy chunk lands and
  // DiscoverPage re-mounts its own skeleton while client data is in
  // flight, painting instantly avoids the 250ms blank "reset" window
  // the old double-delay caused.
  return (
    <main className="scrollbar-hide min-h-screen overflow-y-auto bg-background text-foreground">
      <HeroSkeleton />
      <div className="relative z-10 space-y-2 px-1 py-8 sm:space-y-8 sm:px-8 sm:py-12">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-8 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="space-y-6 sm:space-y-8">
            <PosterRow titleWidth="w-40" />
            <PosterRow titleWidth="w-44" />
            <PosterRow titleWidth="w-32" />
            <MangaPosterRow titleWidth="w-36" />
            <GenresSkeleton />
          </div>
          <aside className="hidden space-y-4 sm:block sm:space-y-6 lg:sticky lg:top-24 lg:self-start">
            <SidebarTopList />
            <SidebarTopList />
            <SidebarPartners />
            <SidebarNews />
            <SidebarCommunities />
          </aside>
        </div>
      </div>
    </main>
  );
}
